import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, ArrowRight, ArrowLeft, Check, Sparkles, Lightbulb, X, Plus, Trash2, PartyPopper,
} from "lucide-react";

export default function PlanCoach({ onExit, onFinish }) {
  const [state, setState] = useState(null);
  const [idx, setIdx] = useState(0);
  const [draft, setDraft] = useState("");
  const [listDraft, setListDraft] = useState([""]);
  const [saving, setSaving] = useState(false);
  const [example, setExample] = useState(null);
  const [exLoading, setExLoading] = useState(false);
  const [done, setDone] = useState(false);

  const load = useCallback(() => {
    api.get("/portal/plan-coach").then((r) => {
      setState(r.data);
      setIdx(r.data.next_index || 0);
    }).catch(() => toast.error("Could not load the coach"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const step = state?.steps?.[idx];

  // hydrate the editor when the step actually changes (not on every state refresh,
  // which would wipe in-progress edits)
  useEffect(() => {
    if (!step) return;
    setExample(null);
    if (step.type === "list") {
      const v = Array.isArray(step.value) && step.value.length ? step.value : [""];
      setListDraft(v);
    } else {
      setDraft(step.value || "");
    }
  }, [idx, step?.field]); // eslint-disable-line

  if (!state) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gold" size={28} /></div>;

  const total = state.steps.length;
  const currentValue = () => (step.type === "list"
    ? listDraft.map((x) => x.trim()).filter(Boolean)
    : draft.trim());

  const hasValue = () => {
    const v = currentValue();
    return step.type === "list" ? v.length > 0 : !!v;
  };

  const saveAndNext = async (goNext = true) => {
    if (!step.optional && !hasValue()) { toast.error("Add an answer to continue (or skip if optional)."); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/portal/plan-coach/answer", { field: step.field, value: currentValue() });
      setState(data);
      if (goNext) {
        if (idx < total - 1) setIdx(idx + 1);
        else setDone(true);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setSaving(false); }
  };

  const skip = () => { if (idx < total - 1) setIdx(idx + 1); else setDone(true); };
  const back = () => { if (idx > 0) setIdx(idx - 1); };

  const getExample = async () => {
    setExLoading(true);
    try {
      const { data } = await api.post("/portal/plan-coach/example", { field: step.field });
      setExample(data.example);
    } catch (e) { toast.error("Could not fetch an example"); } finally { setExLoading(false); }
  };

  const useExample = () => {
    if (!example) return;
    if (step.type === "list") {
      // Split the example into separate entries: newlines first, then common
      // separators (·, ;, •) so a stray paragraph doesn't land in one field.
      let items = example.split(/\r?\n|·|•|;/).map((s) => s.replace(/^[\s\-*\d.)]+/, "").trim()).filter(Boolean);
      if (items.length <= 1) items = example.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
      setListDraft(items.length ? items : [""]);
    } else setDraft(example);
    setExample(null);
  };

  if (done) {
    const fmt = (s) => (s.type === "list" ? (Array.isArray(s.value) ? s.value.join(" · ") : "") : s.value);
    return (
      <div className="max-w-2xl mx-auto" data-testid="plan-coach-done">
        <div className="bg-navy-800 border border-gold/25 p-8 text-center mb-5">
          <PartyPopper className="mx-auto text-gold mb-3" size={38} />
          <div className="text-white font-heading text-2xl mb-2">Your Business Profile is taking shape</div>
          <p className="text-gray-400 max-w-lg mx-auto">
            Nice work{state.org_name ? `, ${state.org_name}` : ""}! Here's everything you shared — review it, tweak anything, then build your plan.
          </p>
        </div>

        <div className="bg-navy-800 border border-white/10 divide-y divide-white/5 mb-6" data-testid="coach-recap">
          {state.steps.map((s, i) => (
            <div key={s.field} className="flex items-start justify-between gap-4 p-4">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-gold/80">{s.title}</div>
                <div className="text-sm text-gray-200 mt-0.5 break-words">{fmt(s) || <span className="text-gray-600 italic">Not answered</span>}</div>
              </div>
              <button onClick={() => { setDone(false); setIdx(i); }} data-testid={`coach-recap-edit-${s.field}`}
                className="text-xs text-gold hover:text-gold-light shrink-0">Edit</button>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center gap-3 flex-wrap">
          <button onClick={() => { onFinish?.(); }} data-testid="plan-coach-to-plan"
            className="inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 font-semibold hover:bg-gold-light transition-colors">
            Build my Business Plan <ArrowRight size={16} />
          </button>
          <button onClick={onExit} data-testid="plan-coach-exit-done" className="text-sm text-gray-400 hover:text-white px-4 py-3">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="plan-coach">
      {/* Progress */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-gray-500">Question {idx + 1} of {total} · Building your Business Profile</div>
        <button onClick={onExit} data-testid="plan-coach-exit" className="text-gray-500 hover:text-white flex items-center gap-1 text-xs"><X size={14} /> Save &amp; exit</button>
      </div>
      <p className="text-[11px] text-gray-500 mb-4 flex items-center gap-1.5" data-testid="coach-autosave-note"><Check size={11} className="text-emerald-400" /> Your answers save as you go — close anytime and pick up right where you left off.</p>
      <div className="h-1.5 bg-white/10 overflow-hidden mb-8">
        <div className="h-full bg-gold transition-all duration-300" style={{ width: `${Math.round(((idx) / total) * 100)}%` }} />
      </div>

      <div className="bg-navy-800 border border-white/10 p-8">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h2 className="text-white font-heading text-2xl leading-tight" data-testid="coach-question">{step.title}</h2>
          {step.optional && <span className="text-[10px] uppercase tracking-wider text-gray-500 border border-white/10 px-2 py-0.5 mt-1 shrink-0">Optional</span>}
        </div>
        <p className="text-sm text-gray-400 mb-5">{step.help}</p>

        {/* Why this matters */}
        <div className="bg-gold/5 border-l-2 border-gold/50 px-4 py-3 mb-6" data-testid="coach-why">
          <div className="text-[11px] uppercase tracking-wider text-gold mb-1 flex items-center gap-1.5"><Lightbulb size={12} /> Why this matters</div>
          <p className="text-sm text-gray-300 leading-relaxed">{step.why}</p>
        </div>

        {/* Input */}
        {step.type === "list" ? (
          <div className="space-y-2 mb-4" data-testid="coach-list-input">
            {listDraft.map((ln, i) => (
              <div key={i} className="flex gap-2">
                <input value={ln} onChange={(e) => { const a = [...listDraft]; a[i] = e.target.value; setListDraft(a); }}
                  data-testid={`coach-list-${i}`} placeholder="Add one…"
                  className="flex-1 bg-navy border border-white/15 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none" />
                {listDraft.length > 1 && <button onClick={() => setListDraft(listDraft.filter((_, x) => x !== i))} className="text-gray-500 hover:text-red-400 px-2"><Trash2 size={15} /></button>}
              </div>
            ))}
            <button onClick={() => setListDraft([...listDraft, ""])} data-testid="coach-list-add" className="inline-flex items-center gap-1 text-xs text-gold hover:text-gold-light"><Plus size={13} /> Add another</button>
          </div>
        ) : step.type === "textarea" ? (
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={4} data-testid="coach-input"
            placeholder="Type your answer…" autoFocus
            className="w-full bg-navy border border-white/15 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none resize-y leading-relaxed mb-4" />
        ) : (
          <input value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="coach-input"
            placeholder="Type your answer…" autoFocus
            className="w-full bg-navy border border-white/15 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none mb-4" />
        )}

        {/* AI example helper */}
        <div className="mb-6">
          <button onClick={getExample} disabled={exLoading} data-testid="coach-example-btn"
            className="inline-flex items-center gap-1.5 text-xs text-gold/90 hover:text-gold">
            {exLoading ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} />} Not sure? Show me an example for my business
          </button>
          {example && (
            <div className="mt-3 bg-navy border border-gold/20 p-4 text-sm text-gray-300" data-testid="coach-example">
              <div className="whitespace-pre-wrap leading-relaxed">{example}</div>
              <div className="mt-3 flex gap-3">
                <button onClick={useExample} data-testid="coach-example-use" className="text-xs bg-gold/15 text-gold border border-gold/30 px-3 py-1.5 hover:bg-gold/25">Use this as a starting point</button>
                <button onClick={() => setExample(null)} className="text-xs text-gray-500 hover:text-white">Dismiss</button>
              </div>
            </div>
          )}
        </div>

        {/* Nav */}
        <div className="flex items-center justify-between pt-4 border-t border-white/10">
          <button onClick={back} disabled={idx === 0} data-testid="coach-back"
            className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white disabled:opacity-40">
            <ArrowLeft size={15} /> Back
          </button>
          <div className="flex items-center gap-3">
            {step.optional && <button onClick={skip} data-testid="coach-skip" className="text-sm text-gray-500 hover:text-white">Skip</button>}
            <button onClick={() => saveAndNext(true)} disabled={saving} data-testid="coach-next"
              className="inline-flex items-center gap-2 bg-gold text-navy px-6 py-2.5 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />}
              {idx < total - 1 ? "Save & Continue" : "Finish"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
